import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  Drawer,
  ErrorState,
  InlineAlert,
  LoadingState,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type {
  AllocationView,
  ReservationView,
  ResourcePersonCapacity,
} from "../../shared/types/api";
import { PROJECT_ROLE_LABELS } from "../projects/projectVocabulary";
import { AllocationDrawer } from "./AllocationDrawer";
import { ReservationDrawer } from "./ReservationDrawer";
import {
  ALLOCATION_STATUS_LABELS,
  RESERVATION_TYPE_LABELS,
  describeUtilisation,
  formatHours,
  formatPercent,
} from "./resourceVocabulary";

/**
 * §45's click-through: what makes up one heatmap cell.
 *
 * <p>Laid out as §40's formula, top to bottom, rather than as a set of totals. A planner looking
 * at 140% needs to know which part of the chain produced it — three projects each asking for half
 * a person is a different problem from one project asking for all of somebody who is on leave, and
 * the two are indistinguishable from the percentage alone.
 */
export function PersonCapacityDrawer({
  target,
  onClose,
  onChanged,
}: {
  target: { employeeId: string; fullName: string; from: string; to: string } | null;
  onClose: () => void;
  /** Called when a reservation is added or ended here, so the planner behind can refresh. */
  onChanged?: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [reserving, setReserving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [allocating, setAllocating] = useState<{
    allocation: AllocationView;
    mode: "adjust" | "close" | "reassign";
  } | null>(null);

  const capacity = useQuery({
    queryKey: ["person-capacity", target?.employeeId, target?.from, target?.to],
    queryFn: () =>
      api.get<ResourcePersonCapacity>(
        `/api/v1/resources/people/${target?.employeeId}/capacity?from=${target?.from}&to=${target?.to}`,
      ),
    enabled: Boolean(target),
  });

  /*
     * §40: the "less training, meetings and admin" line above is a total. This is what it is made
     * of — a deduction nobody can see the reasons for is one people argue with.
     */
  /*
   * The allocations themselves, not the per-project totals the capacity view gives. The totals
   * answer "how full are they"; these answer "which arrangement do I change", which is what
   * somebody looking at an over-allocated week actually needs.
   */
  const allocations = useQuery({
    queryKey: ["person-allocations", target?.employeeId],
    queryFn: () =>
      api.get<AllocationView[]>(
        `/api/v1/resources/people/${target?.employeeId}/allocations`,
      ),
    enabled: Boolean(target),
  });

  const reservations = useQuery({
    queryKey: ["person-reservations", target?.employeeId],
    queryFn: () =>
      api.get<ReservationView[]>(
        `/api/v1/resources/people/${target?.employeeId}/reservations`,
      ),
    enabled: Boolean(target),
  });

  const endReservation = async (id: string) => {
    const today = new Date().toISOString().slice(0, 10);
    setBusy(id);
    try {
      await api.del(`/api/v1/resources/reservations/${id}?end=${today}`);
      await queryClient.invalidateQueries({ queryKey: ["person-reservations"] });
      await queryClient.invalidateQueries({ queryKey: ["person-capacity"] });
      onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  const data = capacity.data;
  const state = data
    ? describeUtilisation(data.utilisation, data.unavailable)
    : { tone: "gray" as const, label: "" };

  return (
    <Drawer
      open={Boolean(target)}
      onClose={onClose}
      title={target?.fullName ?? "Capacity"}
      subtitle={target ? `${target.from} to ${target.to}` : undefined}
      width="lg"
    >
      {capacity.isPending ? (
        <LoadingState label="Working out capacity…" />
      ) : capacity.isError ? (
        <ErrorState error={capacity.error} onRetry={() => void capacity.refetch()} />
      ) : data ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-ink-200 bg-ink-50 px-4 py-3">
            <div>
              <p className="text-sm text-ink-500">Committed against available</p>
              <p
                className={cn(
                  "tnum font-display text-2xl font-semibold",
                  data.overallocated ? "text-red-600" : "text-ink-900",
                )}
              >
                {formatPercent(data.utilisation)}
              </p>
            </div>
            <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
          </div>

          {data.overallocated && (
            <InlineAlert tone="warning">
              {formatHours(data.overallocatedHours)} more has been promised than exists over these
              dates. That is allowed — sometimes it is the right call — but nobody should arrive at
              it by accident.
            </InlineAlert>
          )}

          {data.unavailable && (
            <InlineAlert tone="info">
              Away or fully reserved for this whole period, so there is no capacity here to plan
              against.
            </InlineAlert>
          )}

          {/* §40's formula, in the order it is applied. */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink-900">Where the time goes</h3>
            <div className="overflow-hidden rounded-lg border border-ink-200">
              <Row
                label={`Working days (${data.workingDays})`}
                value={formatHours(data.grossHours)}
              />
              <Row label="Less public holidays" value={minus(data.holidayHours)} deduction />
              <Row label="Less approved leave" value={minus(data.leaveHours)} deduction />
              <Row
                label="Less training, meetings and admin"
                value={minus(data.reservedHours)}
                deduction
              />
              <Row label="Available" value={formatHours(data.availableHours)} strong />
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink-900">Who has claimed it</h3>
            {data.byProject.length === 0 ? (
              <p className="text-sm text-ink-500">
                Nothing allocated over these dates. Their time is unspoken for.
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-ink-200">
                {data.byProject.map((share) => (
                  <button
                    key={share.projectId}
                    type="button"
                    onClick={() => navigate(`/projects/${share.projectId}`)}
                    className="flex w-full items-center gap-3 border-b border-ink-100 px-3 py-2 text-left text-sm transition-colors last:border-0 hover:bg-ink-50"
                  >
                    <span className="font-mono text-xs text-brand-600">{share.projectKey}</span>
                    <span className="min-w-0 flex-1 truncate text-ink-800">
                      {share.projectName}
                    </span>
                    <span className="h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-ink-100">
                      <span
                        className="block h-full rounded-full bg-brand-500"
                        style={{ width: `${Math.min(100, share.percentage)}%` }}
                      />
                    </span>
                    <span className="tnum w-12 shrink-0 text-right text-xs text-ink-600">
                      {Math.round(share.percentage)}%
                    </span>
                    <span className="tnum w-14 shrink-0 text-right text-xs text-ink-500">
                      {formatHours(share.hours)}
                    </span>
                  </button>
                ))}

                <div className="flex items-center gap-3 bg-ink-50 px-3 py-2 text-sm font-medium">
                  <span className="flex-1 text-ink-700">Total</span>
                  <span
                    className={cn(
                      "tnum w-12 text-right",
                      data.overallocated ? "text-red-600" : "text-ink-900",
                    )}
                  >
                    {formatPercent(data.utilisation)}
                  </span>
                  <span className="tnum w-14 text-right text-ink-900">
                    {formatHours(data.allocatedHours)}
                  </span>
                </div>
              </div>
            )}
          </section>

          {(allocations.data ?? []).length > 0 && can("RESOURCE_ALLOCATE") && (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink-900">Their allocations</h3>
              <div className="overflow-hidden rounded-lg border border-ink-200">
                {(allocations.data ?? []).map((allocation) => (
                  <div
                    key={allocation.id}
                    className="flex items-center gap-2 border-b border-ink-100 px-3 py-2 text-sm last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-ink-800">{allocation.projectName}</p>
                      <p className="text-xs text-ink-500">
                        {PROJECT_ROLE_LABELS[allocation.projectRole]} ·{" "}
                        {Math.round(Number(allocation.allocationPercentage))}% ·{" "}
                        {allocation.allocationStart}
                        {allocation.allocationEnd ? ` to ${allocation.allocationEnd}` : " — open"}
                      </p>
                    </div>
                    <StatusBadge tone={allocation.allocationEnd ? "gray" : "green"}>
                      {ALLOCATION_STATUS_LABELS[allocation.allocationStatus]}
                    </StatusBadge>
                    {allocation.allocationEnd == null && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAllocating({ allocation, mode: "adjust" })}
                        >
                          Change
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAllocating({ allocation, mode: "reassign" })}
                        >
                          Move
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAllocating({ allocation, mode: "close" })}
                        >
                          End
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-ink-900">Reserved time</h3>
              {can("RESOURCE_ALLOCATE") && (
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setReserving(true)}
                >
                  Reserve
                </Button>
              )}
            </div>
            {(reservations.data ?? []).length === 0 ? (
              <p className="text-sm text-ink-500">
                Nothing set aside. All of their available time is open to projects.
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-ink-200">
                {(reservations.data ?? []).map((reservation) => (
                  <div
                    key={reservation.id}
                    className="flex items-center gap-3 border-b border-ink-100 px-3 py-2 text-sm last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-ink-800">
                        {RESERVATION_TYPE_LABELS[reservation.reservationType]}
                      </p>
                      <p className="text-xs text-ink-500">
                        {reservation.reservationStart}
                        {reservation.reservationEnd
                          ? ` to ${reservation.reservationEnd}`
                          : " — ongoing"}
                        {reservation.note && ` · ${reservation.note}`}
                      </p>
                    </div>
                    <span className="tnum w-12 shrink-0 text-right text-xs text-ink-600">
                      {Math.round(Number(reservation.percentage))}%
                    </span>
                    {can("RESOURCE_ALLOCATE") && reservation.reservationEnd == null && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={busy === reservation.id}
                        onClick={() => void endReservation(reservation.id)}
                        title="Ends it today. The weeks it did apply to still count."
                      >
                        End
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {!data.overallocated && data.remainingHours > 0 && (
            <p className="text-sm text-ink-500">
              {formatHours(data.remainingHours)} of their time is still unclaimed over these
              dates.
            </p>
          )}
        </div>
      ) : null}

      <AllocationDrawer
        allocation={allocating?.allocation ?? null}
        mode={allocating?.mode ?? "adjust"}
        open={allocating != null}
        onClose={() => setAllocating(null)}
        onSaved={() => onChanged?.()}
      />
      <ReservationDrawer
        employee={target}
        open={reserving}
        onClose={() => setReserving(false)}
        onSaved={() => onChanged?.()}
      />
    </Drawer>
  );
}

function minus(hours: number): string {
  return hours > 0 ? `−${formatHours(hours)}` : "none";
}

function Row({
  label,
  value,
  strong,
  deduction,
}: {
  label: string;
  value: string;
  strong?: boolean;
  deduction?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 border-b border-ink-100 px-3 py-2 text-sm last:border-0",
        strong && "bg-ink-50",
      )}
    >
      <span className={strong ? "font-medium text-ink-900" : "text-ink-500"}>{label}</span>
      <span
        className={cn(
          "tnum",
          strong ? "font-semibold text-ink-900" : deduction ? "text-ink-500" : "text-ink-800",
        )}
      >
        {value}
      </span>
    </div>
  );
}
